/**
 * Automated meta title / description A/B testing.
 *
 * SEO split-testing cannot be done per-visitor: Googlebot must see one
 * version of a <title>/<meta description> at a time, or the test is
 * meaningless (and looks like cloaking). So we run **time-sliced** tests:
 * each experiment rotates its variants on a fixed cycle (default 14 days),
 * deterministically derived from the UTC date. Every render — SSR, client,
 * crawler — resolves to the same variant, with no database read in the hot
 * path.
 *
 * A nightly job (`/api/public/hooks/meta-ab-sync`) pulls Search Console
 * clicks / impressions / CTR / position for each *completed* period and
 * stores them per variant, so the admin dashboard can declare a winner.
 */

export type MetaVariant = {
  id: string;
  title: string;
  description: string;
  /** Optional note describing the hypothesis being tested. */
  hypothesis?: string;
};

export type MetaExperiment = {
  id: string;
  /** Canonical path, always with a trailing slash. */
  path: string;
  label: string;
  /** UTC date (YYYY-MM-DD) the rotation started. */
  startDate: string;
  /** Days each variant stays live before rotating. */
  cycleDays: number;
  variants: MetaVariant[];
  /** Set false to freeze the page on `variants[0]`. */
  active?: boolean;
};

const DAY = 86_400_000;

export const META_EXPERIMENTS: MetaExperiment[] = [
  {
    id: "home-ctr",
    path: "/",
    label: "Homepage",
    startDate: "2026-08-03",
    cycleDays: 14,
    variants: [
      {
        id: "a-ai-citation",
        title: "Austin SEO Agency — Win the Map Pack & Get Cited by AI",
        description:
          "Win the Austin Map Pack and get cited by ChatGPT & Perplexity. Our founder-led SEO agency drives real booked jobs for Texas brands. Book a free audit.",

        hypothesis:
          "AI-citation differentiation plus a concrete free-audit offer lifts CTR at position ~36 where the brand is unknown.",
      },
      {
        id: "b-lead-outcome",
        title: "Austin SEO Agency — More Booked Jobs, Not Just Rankings",
        description:
          "Win the Map Pack and get cited by AI. Our Austin SEO agency focuses on lead generation and booked jobs for Texas businesses. Book a free audit.",

        hypothesis:
          "Outcome-first promise ('more calls') beats capability language for commercial-intent queries.",
      },
    ],
  },
  {

    id: "loc-dallas",
    path: "/locations/dallas/",
    label: "Dallas location page",
    startDate: "2026-08-01",
    cycleDays: 14,
    variants: [
      {
        id: "a-agency",
        title: "Dallas SEO Agency — Win the DFW Local Map Pack",
        description:
          "Win the Dallas Map Pack and get cited by AI search engines across DFW. Senior-led SEO delivery that drives real local leads. Book your audit.",

        hypothesis: "Category term ('SEO agency') matches the dominant Dallas query set.",
      },
      {
        id: "b-outcome",
        title: "SEO Services Dallas TX — Drive More Local Leads & AI Citations",
        description:
          "Dallas SEO focused on revenue. Local Pack visibility, technical fixes, and AI-answer citations across DFW. See your custom growth plan today.",
        hypothesis: "Outcome-led snippet lifts CTR at deep positions where brands mean nothing.",
      },
    ],
  },
  {
    id: "loc-houston",
    path: "/locations/houston/",
    label: "Houston location page",
    startDate: "2026-08-01",
    cycleDays: 14,
    variants: [
      {
        id: "a-agency",
        title: "Houston SEO Agency | Local SEO & AI Search — Optimize Plus",
        description:
          "SEO services in Houston, TX for contractors, clinics and B2B teams. Local Pack, Google Business Profile and AI-answer visibility across the Houston metro.",
      },
      {
        id: "b-outcome",
        title: "SEO Company Houston TX — Local Pack & AI Answer Wins",
        description:
          "Houston SEO that moves the Map Pack and AI answers: neighbourhood-level targeting from the Heights to Katy, with reporting you can actually read.",
      },
    ],
  },
  {
    id: "loc-san-antonio",
    path: "/locations/san-antonio/",
    label: "San Antonio location page",
    startDate: "2026-08-01",
    cycleDays: 14,
    variants: [
      {
        id: "a-agency",
        title: "San Antonio SEO Agency | Local SEO — Optimize Plus",
        description:
          "SEO services in San Antonio, TX. Map Pack rankings, Google Business Profile management and AI-search optimisation for Bexar County businesses.",
      },
      {
        id: "b-outcome",
        title: "SEO Services San Antonio TX — More Calls, Not Just Clicks",
        description:
          "San Antonio SEO focused on booked jobs: Local Pack visibility across Stone Oak, Alamo Heights and the 1604 corridor, plus AI-answer citations.",
      },
    ],
  },
  {
    id: "loc-austin",
    path: "/locations/austin/",
    label: "Austin location page",
    startDate: "2026-08-01",
    cycleDays: 14,
    variants: [
      {
        id: "a-agency",
        title: "Austin SEO Agency — Local SEO & AI Search",
        description:
          "Austin SEO agency for local and B2B growth: Local Pack, technical SEO and AI-answer visibility. Senior strategists, no junior hand-offs. Book now.",
      },
      {
        id: "b-outcome",
        title: "Austin SEO Company — Rank in Maps & AI Answers",
        description:
          "Austin SEO built on the Optimize Framework™: local SEO, technical fixes and content that gets cited by ChatGPT and Perplexity. Book a strategy call.",
      },
    ],
  },
  {
    id: "loc-the-woodlands",
    path: "/locations/the-woodlands/",
    label: "The Woodlands location page",
    startDate: "2026-08-01",
    cycleDays: 14,
    variants: [
      {
        id: "a-agency",
        title: "The Woodlands SEO Agency | Local SEO — Optimize Plus",
        description:
          "SEO services in The Woodlands, TX. Local Pack, Google Business Profile and AI-search programmes for Montgomery County service businesses.",
      },
      {
        id: "b-outcome",
        title: "SEO Services The Woodlands TX — Local Pack & AI Search",
        description:
          "The Woodlands SEO for clinics, home services and professional firms: Map Pack visibility across Creekside, Sterling Ridge, Spring and Conroe.",
      },
    ],
  },
  {
    id: "loc-fort-worth",
    path: "/locations/fort-worth/",
    label: "Fort Worth location page",
    startDate: "2026-08-01",
    cycleDays: 14,
    variants: [
      {
        id: "a-agency",
        title: "Fort Worth SEO Agency | Local SEO — Optimize Plus",
        description:
          "SEO services in Fort Worth, TX. Tarrant County Local Pack rankings, Google Business Profile management and AI-answer visibility.",
      },
      {
        id: "b-outcome",
        title: "SEO Company Fort Worth TX — Map Pack & AI Answers",
        description:
          "Fort Worth SEO for the Cultural District, Alliance and Keller corridors: local rankings, technical fixes and AI-search citations that bring calls.",
      },
    ],
  },
  {
    id: "svc-seo",
    path: "/services/seo/",
    label: "SEO service page",
    startDate: "2026-08-01",
    cycleDays: 14,
    variants: [
      {
        id: "a-service",
        title: "SEO Services in Austin, TX | Optimize Plus",
        description:
          "Full-service SEO: technical, content, local and digital PR, run by senior strategists. Transparent reporting and a plan you see before you commit.",
      },
      {
        id: "b-proof",
        title: "Austin SEO Services That Compound",
        description:
          "SEO programmes engineered around the Optimize Framework™: technical, topical authority and AI-answer visibility. See live client results first.",
      },
    ],
  },
  {
    id: "svc-local-seo",
    path: "/services/local-seo/",
    label: "Local SEO service page",
    startDate: "2026-08-01",
    cycleDays: 14,
    variants: [
      {
        id: "a-service",
        title: "Local SEO Services in Austin & Texas | Optimize Plus",
        description:
          "Local SEO that wins the Map Pack: Google Business Profile optimisation, citations, review velocity and location pages built for real service areas.",
      },
      {
        id: "b-outcome",
        title: "Win the Map Pack — Local SEO for Texas Service Businesses",
        description:
          "Win the 3-pack in the ZIPs that pay. Our local SEO drives Map Pack rankings and AI-answer visibility for Texas service brands. Book an audit.",

      },
    ],
  },
  {
    id: "glossary-ctr",
    path: "/glossary/ctr/",
    label: "Glossary — CTR",
    startDate: "2026-08-01",
    cycleDays: 14,
    variants: [
      {
        id: "a-definition",
        title: "CTR in SEO: What It Means & Benchmarks by Position",
        description:
          "Click-through rate explained for SEO: how it is calculated, average CTR by Google position in 2026, what AI Overviews changed, and how to improve it.",
      },
      {
        id: "b-benchmark",
        title: "Average SEO CTR by Google Ranking Position (2026 Data)",
        description:
          "Position-by-position organic CTR benchmarks, how AI Overviews cut clicks, and the title/description fixes that reliably raise CTR in Search Console.",
      },
    ],
  },
  {
    id: "glossary-dr",
    path: "/glossary/dr/",
    label: "Glossary — DR",
    startDate: "2026-08-01",
    cycleDays: 14,
    variants: [
      {
        id: "a-definition",
        title: "Domain Rating (DR) Explained — Bands, Uses & Limits",
        description:
          "What Domain Rating measures, how DR differs from Domain Authority, realistic DR bands by site type, and how much either metric should influence strategy.",
      },
      {
        id: "b-comparison",
        title: "DR vs DA: Which Domain Metric Actually Matters?",
        description:
          "Domain Rating vs Domain Authority compared: how each is calculated, what good looks like by industry, and why neither is a Google ranking factor.",
      },
    ],
  },
];

const byPath = new Map(META_EXPERIMENTS.map((e) => [e.path, e]));

function utcMidnight(date: string | Date): number {
  if (typeof date === "string") return Date.parse(`${date}T00:00:00Z`);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function toIsoDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Zero-based period index for `now`; negative before the start date. */
export function periodIndexFor(exp: MetaExperiment, now: Date = new Date()): number {
  const diff = utcMidnight(now) - utcMidnight(exp.startDate);
  return Math.floor(diff / (exp.cycleDays * DAY));
}

export function periodWindow(exp: MetaExperiment, index: number): { start: string; end: string } {
  const startMs = utcMidnight(exp.startDate) + index * exp.cycleDays * DAY;
  return {
    start: toIsoDate(startMs),
    end: toIsoDate(startMs + (exp.cycleDays - 1) * DAY),
  };
}

export function variantForPeriod(exp: MetaExperiment, index: number): MetaVariant {
  if (exp.active === false || index < 0) return exp.variants[0];
  return exp.variants[index % exp.variants.length];
}

export type ResolvedMeta = {
  title: string;
  description: string;
  experimentId: string;
  variantId: string;
  periodIndex: number;
};

/**
 * Resolve the live title/description for a path. Returns `null` when the
 * path has no experiment, so callers fall back to their static copy.
 */
export function resolveMetaExperiment(path: string, now: Date = new Date()): ResolvedMeta | null {
  const key = path.endsWith("/") ? path : `${path}/`;
  const exp = byPath.get(key);
  if (!exp) return null;
  const index = periodIndexFor(exp, now);
  const variant = variantForPeriod(exp, index);
  return {
    title: variant.title,
    description: variant.description,
    experimentId: exp.id,
    variantId: variant.id,
    periodIndex: Math.max(index, 0),
  };
}

/** Convenience for route heads: experiment copy when present, else the static copy. */
export function abMeta(
  path: string,
  fallback: { title: string; description: string },
): { title: string; description: string } {
  const r = resolveMetaExperiment(path);
  return r ? { title: r.title, description: r.description } : fallback;
}

export function getExperiment(id: string): MetaExperiment | undefined {
  return META_EXPERIMENTS.find((e) => e.id === id);
}
